import { googleMapsApiKey, googleMapsEnabled } from 'src/config'

const placesBaseUrl = 'https://places.googleapis.com/v1/places'

export default async ({ app }) => {
  if (!googleMapsEnabled) return

  function getUnitSystem () {
    const lang = navigator.language || navigator.userLanguage
    if (lang.startsWith('en-US') || lang.startsWith('en-GB')) {
      return 'mi'
    } else {
      return 'km'
    }
  }

  function getPlaceDetails (placeId) {
    const endpoint = `${placesBaseUrl}/${placeId}?fields=location&key=${googleMapsApiKey}`

    return fetch(endpoint)
      .then(response => response.json())
      .then(data => {
        return {
          lat: data.location.latitude,
          lon: data.location.longitude
        }
      })
      .catch(error => {
        console.error('Error fetching place details:', error)
        return null
      })
  }

  function getPlaceAddressDetails (placeId) {
    const endpoint = `${placesBaseUrl}/${placeId}?fields=addressComponents,formattedAddress&key=${googleMapsApiKey}`

    return fetch(endpoint)
      .then(response => response.json())
      .then(data => {
        return {
          address_components: data.addressComponents,
          formatted_address: data.formattedAddress
        }
      })
      .catch(error => {
        console.error('Error fetching place details:', error)
        return null
      })
  }

  function getPlacesFromAddress (address, countries) {
    if (!address) return Promise.resolve([])

    const endpoint = `${placesBaseUrl}:autocomplete`

    return fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': googleMapsApiKey,
        'X-Goog-FieldMask': '*'
      },
      body: JSON.stringify({
        input: address,
        languageCode: 'en',
        includedRegionCodes: countries
      })
    })
      .then(response => response.json())
      .then(data => {
        return data.suggestions.map(suggestion => ({
          address: suggestion.placePrediction.text.text,
          placeId: suggestion.placePrediction.placeId
        }))
      })
      .catch(error => {
        console.error('Error fetching places:', error)
        return []
      })
  }

  function getClosestLocations (origin, locations) {
    if (!origin || !locations || !locations.length) return null

    const targetLat = origin.lat
    const targetLon = origin.lon

    if (!targetLat || !targetLon) return null

    const unitSystem = getUnitSystem()

    // Haversine formula to calculate distance between two points on Earth
    function calculateDistance (lat1, lon1, lat2, lon2) {
      const R = 6371 // Earth's radius in kilometers
      const dLat = toRad(lat2 - lat1)
      const dLon = toRad(lon2 - lon1)
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      const km = R * c
      const miles = km * 0.621371 // Convert kilometers to miles
      return {
        km: Math.round(km * 10) / 10, // Round to 1 decimal place
        miles: Math.round(miles * 10) / 10 // Round to 1 decimal place
      }
    }

    function toRad (degrees) {
      return degrees * (Math.PI / 180)
    }

    const locationsWithDistance = []
    // Calculate distance for each location and sort by distance
    locations.forEach(location => {
      if (!location.lat || !location.lon) return null
      const distance = calculateDistance(
        targetLat,
        targetLon,
        location.lat,
        location.lon
      )

      // Set the distance values based on the unit system
      const distanceValue = unitSystem === 'mi' ? distance.miles : distance.km
      const distanceText = `${distanceValue} ${unitSystem}`

      // Use setters for the distance properties
      location.currentDistanceValue = distanceValue
      location.currentDistanceText = distanceText

      locationsWithDistance.push(location)
    })

    // Sort locations by distance (closest first)
    return locationsWithDistance.sort((a, b) => a.currentDistanceValue - b.currentDistanceValue)
  }

  app.config.globalProperties.$getClosestLocations = getClosestLocations
  app.config.globalProperties.$getPlacesFromAddress = getPlacesFromAddress
  app.config.globalProperties.$getPlaceDetails = getPlaceDetails
  app.config.globalProperties.$getPlaceAddressDetails = getPlaceAddressDetails
}
